import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  TextField,
  Stack,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";

const statusColor = (status) => {
  switch (status) {
    case "new":
      return "info";
    case "contacted":
      return "warning";
    case "replied":
      return "primary";
    case "converted":
      return "success";
    default:
      return "default";
  }
};

const MobileHelpSupport = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("new");
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("admin/mobile-help-support");
      if (res.data?.success) setRequests(res.data.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load Help & Support requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const openRow = async (row) => {
    try {
      const res = await api.get(`admin/mobile-help-support/${row.id}`);
      if (res.data?.success) {
        setSelected(res.data.data);
        setStatus(res.data.data.status ?? "new");
        setReplySubject(
          res.data.data.topic
            ? `Re: Help & Support — ${res.data.data.topic}`
            : "Re: Your Help & Support request"
        );
        setReplyMessage("");
        setOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load request details.");
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setSelected(null);
    setReplyMessage("");
  };

  const handleSaveStatus = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await api.patch(`admin/mobile-help-support/${selected.id}/status`, { status });
      toast.success("Status updated.");
      await fetchRequests();
      const res = await api.get(`admin/mobile-help-support/${selected.id}`);
      if (res.data?.success) setSelected(res.data.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  const handleSendReply = async () => {
    if (!selected?.id) return;
    if (replyMessage.trim().length < 10) {
      toast.error("Reply must be at least 10 characters.");
      return;
    }
    if (!selected.email) {
      toast.error("No contact email on this request.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`admin/mobile-help-support/${selected.id}/reply`, {
        message: replyMessage.trim(),
        subject: replySubject.trim() || undefined,
      });
      toast.success(res.data?.message || "Reply sent.");
      await fetchRequests();
      const detail = await api.get(`admin/mobile-help-support/${selected.id}`);
      if (detail.data?.success) {
        setSelected(detail.data.data);
        setStatus(detail.data.data.status ?? "replied");
      }
      setReplyMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Mobile Help &amp; Support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Messages sent from the student mobile app (Profile → Help &amp; Support). Open a row to read the full
        message and email a reply to the student.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Website chat bot conversations (including chats escalated to the team) are in{" "}
        <RouterLink to="/admin/support-chat">Support Chats</RouterLink>.
      </Typography>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No Help &amp; Support messages yet.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8f9ff" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Topic</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>{r.email || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{r.topic || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={statusColor(r.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.created_at ? new Date(r.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => openRow(r)}>
                        View &amp; reply
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open && !!selected} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Help &amp; Support #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {selected.name}
              </Typography>
              <Typography variant="body2">
                <strong>Contact email:</strong> {selected.email || "—"}
              </Typography>
              {selected.mobile_user_id ? (
                <Typography variant="body2">
                  <strong>Mobile user ID:</strong> {selected.mobile_user_id}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Topic:</strong> {selected.topic || "—"}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                <strong>Student message:</strong>
                {"\n"}
                {selected.message || "—"}
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                  <MenuItem value="replied">Replied</MenuItem>
                  <MenuItem value="converted">Converted</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={handleSaveStatus} disabled={saving}>
                {saving ? "Saving…" : "Update status"}
              </Button>

              <Divider />

              <Typography variant="subtitle2" fontWeight={700}>
                Send email reply
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Email subject"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Your reply to the student"
                placeholder="Type your reply (min 10 characters)…"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                helperText={`${replyMessage.length}/5000`}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSendReply}
            disabled={sending || !selected?.email}
          >
            {sending ? "Sending…" : "Send reply by email"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileHelpSupport;
